/**
 * useSpeechRecognition
 * Thin wrapper around the browser Web Speech API for chat dictation. Reports
 * `isSupported = false` on browsers that lack SpeechRecognition (e.g. Firefox)
 * so callers can hide their mic affordance instead of presenting a dead button.
 */

import { useCallback, useEffect, useRef, useState } from "react"

interface SpeechRecognitionAlternative {
  transcript: string
}

interface SpeechRecognitionResult {
  readonly length: number
  readonly isFinal: boolean
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
}

interface SpeechRecognitionResultList {
  readonly length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number
  readonly results: SpeechRecognitionResultList
}

interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string
}

interface SpeechRecognition extends EventTarget {
  lang: string
  continuous: boolean
  interimResults: boolean
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
  abort: () => void
}

type SpeechRecognitionConstructor = new () => SpeechRecognition

function getSpeechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === "undefined") return null
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructor
    webkitSpeechRecognition?: SpeechRecognitionConstructor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

interface UseSpeechRecognitionOptions {
  onTranscript: (text: string) => void
  lang?: string
}

interface UseSpeechRecognitionReturn {
  isSupported: boolean
  isRecording: boolean
  start: () => void
  stop: () => void
  toggle: () => void
}

export function useSpeechRecognition({
  onTranscript,
  lang = "en-US",
}: UseSpeechRecognitionOptions): UseSpeechRecognitionReturn {
  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const onTranscriptRef = useRef(onTranscript)
  const [isRecording, setIsRecording] = useState(false)

  // Keep the latest callback without recreating the recognizer (which would
  // drop in-flight transcripts).
  useEffect(() => {
    onTranscriptRef.current = onTranscript
  }, [onTranscript])

  const Constructor = getSpeechRecognitionConstructor()
  const isSupported = Constructor !== null

  useEffect(() => {
    if (!Constructor) return

    const recognition = new Constructor()
    recognition.lang = lang
    recognition.continuous = true
    recognition.interimResults = false

    recognition.onresult = event => {
      let chunk = ""
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i]
        if (result.isFinal) chunk += result[0].transcript
      }
      if (chunk) onTranscriptRef.current(chunk)
    }
    recognition.onerror = () => setIsRecording(false)
    recognition.onend = () => setIsRecording(false)

    recognitionRef.current = recognition
    return () => {
      recognition.onresult = null
      recognition.onerror = null
      recognition.onend = null
      recognition.abort()
      recognitionRef.current = null
    }
  }, [Constructor, lang])

  const start = useCallback(() => {
    const recognition = recognitionRef.current
    if (!recognition || isRecording) return
    try {
      recognition.start()
      setIsRecording(true)
    } catch {
      // start() throws if called while already running; ignore.
    }
  }, [isRecording])

  const stop = useCallback(() => {
    const recognition = recognitionRef.current
    if (!recognition) return
    recognition.stop()
  }, [])

  const toggle = useCallback(() => {
    if (isRecording) stop()
    else start()
  }, [isRecording, start, stop])

  return { isSupported, isRecording, start, stop, toggle }
}
